import { Box, Grid, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { format, isSameMonth, isToday } from "date-fns"
import React, { useCallback, useMemo, useState } from "react"

import { useCurrentUser } from "@/hooks/useCurrentUser"
import { WEEKDAYS } from "../constants"
import { useEventManager, useGetMonthData, useWindowResize } from "../hooks"
import { MoreEventsTooltip } from "../styled"
import { calculateMaxChars } from "../utils"
import CalendarEvent from "./CalendarEvent"
import MoreEventsModal from "./MoreEventsModal"

/**
 * Month Calendar Component - displays a calendar with events
 */
const MonthCalendar = ({ calendarData = {}, month = new Date(), filters = {} }) => {
  const { currentUser } = useCurrentUser()
  const [maxChars, setMaxChars] = useState(calculateMaxChars)
  const { events = [] } = calendarData
  const [selectedDayEvents, setSelectedDayEvents] = useState([])
  const [selectedDay, setSelectedDay] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const { calendarDays } = useGetMonthData(month)
  const eventManager = useEventManager(events)

  // Use debounced resize handler
  useWindowResize(
    useCallback(() => {
      setMaxChars(calculateMaxChars())
    }, [])
  )

  // Event handlers
  const handleEventClick = useCallback(event => {
    if (event.link) window.open(event.link, "_blank")
  }, [])

  const openModal = useCallback((dayEvents, day) => {
    setSelectedDayEvents(dayEvents)
    setSelectedDay(day)
    setModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setModalOpen(false)
    setSelectedDay(null)
  }, [])

  // Day events renderer
  const renderEventsForDay = useCallback(
    day => {
      const formattedDay = format(day, "yyyy-MM-dd")
      const dayEvents = eventManager.getEventsForDay(formattedDay)

      const visibleEvents = dayEvents.slice(0, 5)
      const hiddenCount = dayEvents.length - visibleEvents.length

      return (
        <>
          {visibleEvents.map(event => (
            <CalendarEvent
              key={event.id}
              event={event}
              maxChars={maxChars}
              onClick={handleEventClick}
              userTimezone={currentUser?.user_time_zone_abbr}
            />
          ))}

          {hiddenCount > 0 && (
            <MoreEventsTooltip title={`${hiddenCount} more events`} placement="top" arrow>
              <Box
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  textAlign: "left",
                  width: "100%",
                  textDecoration: "underline",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: theme => theme.palette.btBlue[20],
                    color: "inherit",
                  },
                }}
                onClick={() => openModal(dayEvents, day)}
              >
                + {hiddenCount} more
              </Box>
            </MoreEventsTooltip>
          )}
        </>
      )
    },
    [eventManager, maxChars, handleEventClick, openModal, currentUser]
  )

  // Create enhanced filters with date information
  const enhancedFilters = useMemo(
    () => ({
      ...filters,
      selectedDay: selectedDay ? format(selectedDay, "yyyy-MM-dd") : null,
      formattedDay: selectedDay ? format(selectedDay, "MMMM d, yyyy") : null,
    }),
    [filters, selectedDay]
  )

  return (
    <>
      <Grid container columns={7} sx={{ border: "1px solid #e0e0e0", borderBottom: "none", borderRight: "none" }}>
        {/* Calendar Header */}
        {WEEKDAYS.map(weekday => (
          <Grid item xs={1} key={weekday} sx={{ height: "2.75rem" }}>
            <Typography
              variant="subtitle2"
              align="center"
              sx={{
                fontWeight: "normal",
                borderBottom: "1px solid #e0e0e0",
                padding: "10px 0",
                borderRight: "1px solid #e0e0e0",
              }}
            >
              {weekday}
            </Typography>
          </Grid>
        ))}

        {/* Calendar Days */}
        {calendarDays.map(day => (
          <Grid
            item
            xs={1}
            key={day.toString()}
            sx={{
              borderRight: "1px solid #e0e0e0",
              borderBottom: "1px solid #e0e0e0",
              minHeight: "170px",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              padding: "4px",
              overflow: "hidden",
              bgcolor: isSameMonth(day, month) ? "transparent" : alpha("#f3f3f9", 0.3),
              "&:hover": {
                boxShadow: isSameMonth(day, month) ? "0 0 10px 0 rgba(0, 0, 0, 0.1)" : "none",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: "500",
                alignSelf: "flex-end",
                width: 24,
                height: 24,
                lineHeight: "24px",
                textAlign: "center",
                borderRadius: "50%",
                backgroundColor: isToday(day) ? "red" : "transparent",
                color: isSameMonth(day, month) ? (isToday(day) ? "#fff" : "black") : "#ccc",
              }}
            >
              {format(day, "d")}
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                marginTop: "4px",
                width: "100%",
                overflow: "hidden",
              }}
            >
              {renderEventsForDay(day)}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Events Modal */}
      <MoreEventsModal isOpen={modalOpen} onClose={closeModal} events={selectedDayEvents} filters={enhancedFilters} />
    </>
  )
}

export default React.memo(MonthCalendar)
